import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  LogOut,
  LayoutDashboard,
  Settings,
  Bell,
  Search,
  Menu,
  X,
  Package,
  Layers,
  Warehouse,
  ShoppingCart,
  DollarSign,
  FileText,
  ChevronDown,
  ChevronUp,
  Users,
  Truck,
  FileCheck,
  PieChart,
  ShoppingBag,
  CreditCard,
  Wallet,
  Banknote,
  Landmark,
  ArrowRightLeft,
  BookOpen,
  Activity,
  TrendingUp,
  Blocks,
  ClipboardList,
  Palette,
  Check,
  Dot,
  Boxes,
  Archive,
  Gift,
  Factory,
  Tags,
  FilePlus,
  Plus,
  Receipt,
  Handshake,
  List,
  ArrowDownCircle,
  User,
  GitMerge,
  Building,
  ArrowDownRight,
  ArrowUpRight,
  RefreshCw,
  ClipboardCheck,
  Clock,
  PackageX,
  LogIn,
  FileEdit,
  Briefcase,
  FolderOpen,
  Database,
  Save,
} from "lucide-react";
import {
  useNavigate,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../hooks/useTheme";
import { ThemeToggle } from "../components/ThemeToggle";
import { Overview } from "./admin/Overview";
import { Categories } from "./admin/Categories";
import { Products } from "./admin/Products";
import { Packages } from "./admin/Packages";
import { Packaging } from "./admin/Packaging";
import { Manufacturers } from "./admin/Manufacturers";
import { useCustomTheme } from "../contexts/ThemeCustomizationContext";

interface MenuItem {
  name: string;
  id?: string;
  icon?: any;
  path?: string;
  subItems?: MenuItem[];
}

import { DatabaseCheck } from "./admin/DatabaseCheck";
import { DatabaseBackup } from "./admin/DatabaseBackup";
import { ProductDetails } from "./admin/ProductDetails";
import { PcbOrderForm } from "./admin/PcbOrderForm";
import { PcbOrdersList } from "./admin/PcbOrdersList";

export const AdminDashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["products"]);
  const location = useLocation();

  const { user, logoutState } = useAuth();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { customTheme, updateTheme } = useCustomTheme();

  const [pcbLanguage, setPcbLanguage] = useState<"en" | "fa">(() => {
    return (localStorage.getItem("pcb-language") as "en" | "fa") || "en";
  });

  const togglePcbLanguage = () => {
    const next = pcbLanguage === "en" ? "fa" : "en";
    setPcbLanguage(next);
    localStorage.setItem("pcb-language", next);
    window.dispatchEvent(new Event("pcb-language-changed"));
  };

  const colorPresets = [
    { name: "آبی", primary: "#2563eb" },
    { name: "زمردی", primary: "#10b981" },
    { name: "بنفش", primary: "#8b5cf6" },
    { name: "نارنجی", primary: "#f97316" },
  ];

  const handleLogout = () => {
    logoutState();
    navigate("/login");
  };

  const username = user?.username || "مدیر";

    const toggleMenu = (id: string | undefined) => {
    if (!id) return;
    setExpandedMenus((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const menuItems: MenuItem[] = [
    { name: "پیشخوان", id: "overview", icon: LayoutDashboard, path: "/admin" },
    {
      name: "سفارش‌ها",
      id: "orders",
      icon: ClipboardList,
      subItems: [
        { name: "سفارش قطعات", icon: Boxes, path: "/admin/orders/parts" },
        { 
          name: "سفارش PCB", 
          id: "pcb_orders",
          icon: Layers, 
          subItems: [
            { name: "سفارشات جدید", icon: Plus, path: "/admin/orders/pcb/new-orders" },
            { name: "ثبت سفارش PCB", icon: FilePlus, path: "/admin/orders/pcb/add" },
            { name: "سفارش‌های جاری", icon: Clock, path: "/admin/orders/pcb/current" },
            { name: "سفارش‌ها", icon: List, path: "/admin/orders/pcb/all" },
          ]
        },
        { name: "سفارش تولید", icon: Factory, path: "/admin/orders/production" },
      ],
    },
    {
      name: "کالا",
      id: "products",
      icon: Package,
      subItems: [
        { name: "کالاها", icon: Boxes, path: "/admin/products" },
        { name: "پکیج", icon: Archive, path: "/admin/products/packages" },
        { name: "بسته‌بندی‌ها", icon: Gift, path: "/admin/products/packaging" },
        {
          name: "سازندگان",
          icon: Factory,
          path: "/admin/products/manufacturers",
        },
        { name: "دسته‌بندی‌ها", icon: Tags, path: "/admin/categories" },
      ],
    },
    {
      name: "مالی",
      id: "finance",
      icon: DollarSign,
      subItems: [
        {
          name: "پرداخت",
          id: "payment",
          icon: CreditCard,
          subItems: [
            {
              id: "pay_reg",
              name: "ثبت پرداختی",
              icon: FilePlus,
              path: "/admin/finance/register-payment",
            },
            {
              id: "exp_reg",
              name: "ثبت هزینه",
              icon: Receipt,
              path: "/admin/finance/register-expense",
            },
            {
              id: "srv_prov_reg",
              name: "ثبت ارائه خدمات",
              icon: Handshake,
              path: "/admin/finance/register-service-provided",
            },
            {
              id: "pays",
              name: "پرداختی‌ها",
              icon: List,
              path: "/admin/finance/payments",
            },
          ],
        },
        {
          name: "دریافت",
          id: "receipts_menu",
          icon: Wallet,
          subItems: [
            {
              id: "rec_reg",
              name: "ثبت دریافتی",
              icon: FilePlus,
              path: "/admin/finance/register-receipt",
            },
            {
              id: "inc_reg",
              name: "ثبت درآمد",
              icon: ArrowDownCircle,
              path: "/admin/finance/register-income",
            },
            {
              id: "srv_rec_reg",
              name: "ثبت دریافت خدمات",
              icon: Handshake,
              path: "/admin/finance/register-service-received",
            },
            {
              id: "recs",
              name: "دریافتی‌ها",
              icon: List,
              path: "/admin/finance/receipts",
            },
          ],
        },
        {
          name: "صندوق",
          id: "cash-registers",
          icon: Banknote,
          path: "/admin/finance/cash-registers",
        },
        {
          name: "حساب",
          id: "accounts",
          icon: Landmark,
          subItems: [
            {
              id: "acc_pers",
              name: "حساب‌های شخصی",
              icon: User,
              path: "/admin/finance/personal-accounts",
            },
            {
              id: "acc_oth",
              name: "حساب‌های دیگران",
              icon: Users,
              path: "/admin/finance/others-accounts",
            },
          ],
        },
        {
          name: "انتقال",
          id: "transfers_menu",
          icon: ArrowRightLeft,
          subItems: [
            {
              id: "tr_2step",
              name: "انتقال دو مرحله‌ای",
              icon: GitMerge,
              path: "/admin/finance/two-step-transfer",
            },
            {
              id: "tr_inter",
              name: "انتقال بین بانکی",
              icon: Building,
              path: "/admin/finance/interbank-transfer",
            },
            {
              id: "tr_a2c",
              name: "انتقال از حساب به صندوق",
              icon: ArrowDownRight,
              path: "/admin/finance/account-to-cash",
            },
            {
              id: "tr_c2a",
              name: "انتقال از صندوق به حساب",
              icon: ArrowUpRight,
              path: "/admin/finance/cash-to-account",
            },
            {
              id: "trs",
              name: "انتقالی‌ها",
              icon: List,
              path: "/admin/finance/transfers",
            },
          ],
        },
        {
          name: "دفتر معین",
          id: "subsidiary-ledger",
          icon: BookOpen,
          path: "/admin/finance/subsidiary-ledger",
        },
        {
          name: "گردش مالی",
          id: "financial-turnover",
          icon: Activity,
          path: "/admin/finance/financial-turnover",
        },
        {
          name: "درآمد و هزینه",
          id: "income-and-expenses",
          icon: TrendingUp,
          path: "/admin/finance/income-and-expenses",
        },
      ],
    },
    {
      name: "خرید",
      id: "purchase",
      icon: ShoppingBag,
      subItems: [
        {
          id: "pur_reg",
          name: "ثبت فاکتور خرید",
          icon: FilePlus,
          path: "/admin/purchase/register-invoice",
        },
        {
          id: "pur_inv",
          name: "فاکتورهای خرید",
          icon: FileText,
          path: "/admin/purchase/invoices",
        },
        {
          id: "pur_turn",
          name: "گردش خرید",
          icon: RefreshCw,
          path: "/admin/purchase/turnover",
        },
      ],
    },
    {
      name: "فروش",
      id: "sales",
      icon: ShoppingCart,
      subItems: [
        {
          id: "sal_reg",
          name: "ثبت فاکتور فروش",
          icon: FilePlus,
          path: "/admin/sales/register-invoice",
        },
        {
          id: "sal_inv",
          name: "فاکتورهای فروش",
          icon: FileText,
          path: "/admin/sales/invoices",
        },
        {
          id: "sal_turn",
          name: "گردش فروش",
          icon: RefreshCw,
          path: "/admin/sales/turnover",
        },
        {
          id: "shp_reg",
          name: "ثبت فاکتور محموله",
          icon: FilePlus,
          path: "/admin/sales/register-shipment-invoice",
        },
        {
          id: "shp_inv",
          name: "فاکتورهای محموله",
          icon: FileText,
          path: "/admin/sales/shipment-invoices",
        },
        {
          id: "shp_turn",
          name: "گردش محموله",
          icon: RefreshCw,
          path: "/admin/sales/shipment-turnover",
        },
        {
          id: "clr_reg",
          name: "ثبت فاکتور ترخیص",
          icon: FilePlus,
          path: "/admin/sales/register-clearance-invoice",
        },
        {
          id: "clr_inv",
          name: "فاکتورهای ترخیص",
          icon: FileText,
          path: "/admin/sales/clearance-invoices",
        },
        {
          id: "clr_turn",
          name: "گردش ترخیص",
          icon: RefreshCw,
          path: "/admin/sales/clearance-turnover",
        },
        {
          id: "prf_reg",
          name: "پیش فاکتور",
          icon: FileEdit,
          path: "/admin/sales/proforma",
        },
        {
          id: "prf_inv",
          name: "پیش فاکتورها",
          icon: FileText,
          path: "/admin/sales/proformas",
        },
      ],
    },
    {
      name: "انبار",
      id: "warehouse",
      icon: Warehouse,
      subItems: [
        {
          id: "wh_list",
          name: "انبارها",
          icon: List,
          path: "/admin/warehouses",
        },
        {
          id: "wh_transfer",
          name: "انتقال بین انبارها",
          icon: ArrowRightLeft,
          path: "/admin/warehouses/transfer",
        },
        {
          id: "wh_inventory",
          name: "انبارگردانی",
          icon: ClipboardCheck,
          path: "/admin/warehouses/inventory",
        },
        {
          id: "wh_temp",
          name: "انبار موقت",
          icon: Clock,
          path: "/admin/warehouses/temp",
        },
        {
          id: "wh_unsent",
          name: "کالاهای ارسال نشده",
          icon: PackageX,
          path: "/admin/warehouses/unsent-products",
        },
        {
          id: "wh_in_receipt",
          name: "حواله‌های ورود",
          icon: LogIn,
          path: "/admin/warehouses/in-receipts",
        },
        {
          id: "wh_in_receipt_manual",
          name: "حواله‌های ورود دستی",
          icon: FileEdit,
          path: "/admin/warehouses/manual-in-receipts",
        },
        {
          id: "wh_out_receipt",
          name: "حواله‌های خروج",
          icon: LogOut,
          path: "/admin/warehouses/out-receipts",
        },
        {
          id: "wh_out_receipt_manual",
          name: "حواله‌های خروج دستی",
          icon: FileEdit,
          path: "/admin/warehouses/manual-out-receipts",
        },
      ],
    },
    {
      name: "اشخاص",
      id: "people",
      icon: Users,
      subItems: [
        {
          id: "ppl_suppliers",
          name: "تامین‌کنندگان",
          icon: Briefcase,
          path: "/admin/people/suppliers",
        },
        {
          id: "ppl_buyers",
          name: "خریداران",
          icon: ShoppingBag,
          path: "/admin/people/buyers",
        },
        {
          id: "ppl_transport",
          name: "باربری‌ها",
          icon: Truck,
          path: "/admin/people/transports",
        },
        {
          id: "ppl_all",
          name: "همه‌ی اشخاص",
          icon: Users,
          path: "/admin/people/all",
        },
        {
          id: "ppl_projects",
          name: "پروژه‌ها",
          icon: FolderOpen,
          path: "/admin/projects",
        },
      ],
    },
    {
      name: "حمل/ترخیص",
      id: "shipping",
      icon: Truck,
      subItems: [
        {
          id: "ship_new",
          name: "ثبت محموله جدید",
          icon: FilePlus,
          path: "/admin/shipping/new-shipment",
        },
        {
          id: "ship_list",
          name: "محموله‌ها",
          icon: List,
          path: "/admin/shipping/shipments",
        },
        {
          id: "clr_new",
          name: "ثبت ترخیص جدید",
          icon: FilePlus,
          path: "/admin/shipping/new-clearance",
        },
        {
          id: "clr_list",
          name: "ترخیصی‌ها",
          icon: List,
          path: "/admin/shipping/clearances",
        },
      ],
    },
    { name: "ابزارک", id: "widgets", icon: Blocks, path: "/admin/widgets" },
    { name: "گزارش", id: "reports", icon: PieChart, path: "/admin/reports" },
    {
      name: "اعلان",
      id: "notifications",
      icon: Bell,
      path: "/admin/notifications",
    },
    {
      name: "تنظیمات",
      id: "settings",
      icon: Settings,
      subItems: [
        {
          name: "بررسی دیتابیس",
          icon: Database,
          path: "/admin/settings/db-check",
        },
        { name: "کپی دیتابیس", icon: Save, path: "/admin/settings/db-backup" },
        {
          name: "تنظیمات عمومی",
          icon: Settings,
          path: "/admin/settings/general",
        },
        { name: "شخصی‌سازی", id: "theme-customization", icon: Palette },
      ],
    },
  ];

  // Map path to a title for the header
  const getPageTitle = () => {
    if (location.pathname === "/admin") return "پیشخوان";
    if (location.pathname.match(/^\/admin\/products\/\d+$/)) return "اطلاعات کالا";
    if (location.pathname === "/admin/orders/pcb/add") {
      return pcbLanguage === "en" ? "PCB Order Registration" : "ثبت سفارش PCB";
    }

    const findTitle = (items: MenuItem[]): string | null => {
      for (const item of items) {
        if (item.path === location.pathname) return item.name;
        if (item.subItems) {
          const found = findTitle(item.subItems);
          if (found) return found;
        }
      }
      return null;
    };

    const title = findTitle(menuItems);
    return title || "داشبورد مدیریت";
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-[#f0f2f5] dark:bg-slate-900 flex transition-colors duration-300 font-sans text-slate-900 dark:text-slate-100"
    >
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: isSidebarOpen ? 280 : 88 }}
        className="bg-[#1e293b] dark:bg-slate-950 text-white flex flex-col p-6 sticky top-0 h-screen shadow-xl z-20 overflow-y-auto overflow-x-hidden custom-scrollbar"
      >
        <div className="flex items-center justify-between mb-8 overflow-hidden whitespace-nowrap">
          {isSidebarOpen && (
            <div className="flex items-center space-x-3 space-x-reverse">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: "var(--admin-primary)" }}
              >
                <span className="font-bold text-xl text-white">A</span>
              </div>
              <span className="text-xl font-bold tracking-tight">
                سیستم مدیریت
              </span>
            </div>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors mr-auto text-slate-300 shrink-0"
          >
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="flex-1 space-y-1.5">
          {menuItems.map((item) => {
            const renderMenuItem = (item: MenuItem, level: number = 0) => {
              const isPathActive = (
                item: MenuItem,
                currentPath: string,
              ): boolean => {
                if (item.path && item.path === currentPath) return true;
                if (item.subItems) {
                  return item.subItems.some((sub) =>
                    isPathActive(sub, currentPath),
                  );
                }
                return false;
              };

              const isActive = isPathActive(item, location.pathname);
              const isExpanded = expandedMenus.includes(item.id || item.name);

              if (item.id === "theme-customization") {
                return (
                  <div
                    key={item.id || item.name}
                    className={level > 0 ? "mt-1 relative" : "relative"}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMenu(item.id || item.name);
                      }}
                      className={`w-full flex items-center justify-between transition-all ${
                        level === 0
                          ? "py-3 px-3 rounded-xl hover:bg-white/5"
                          : "py-2 px-2 text-sm rounded-lg hover:bg-white/5 hover:text-white text-slate-400"
                      }`}
                    >
                      <div
                        className={`flex items-center ${level === 0 ? "gap-4" : "gap-2"}`}
                      >
                        {item.icon ? (
                          <item.icon
                            size={level === 0 ? 20 : 16}
                            className="shrink-0"
                          />
                        ) : (
                          <Dot
                            size={level === 0 ? 24 : 20}
                            strokeWidth={3}
                            className="shrink-0 opacity-40"
                          />
                        )}
                        {isSidebarOpen && (
                          <span
                            className={`${level === 0 ? "font-medium" : ""} text-slate-300 whitespace-nowrap`}
                          >
                            {item.name}
                          </span>
                        )}
                      </div>

                      {isSidebarOpen && isExpanded && (
                        <div
                          className="flex items-center gap-1 mr-auto px-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {colorPresets.map((preset) => (
                            <div
                              key={preset.name}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateTheme({ primaryColor: preset.primary });
                              }}
                              className="w-3 h-3 rounded-full relative transition-transform hover:scale-125 flex items-center justify-center cursor-pointer"
                              style={{ backgroundColor: preset.primary }}
                              title={preset.name}
                            >
                              {customTheme.primaryColor === preset.primary && (
                                <Check
                                  size={8}
                                  className="text-white drop-shadow-md"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </button>
                  </div>
                );
              }

              if (item.subItems) {
                return (
                  <div
                    key={item.id || item.name}
                    className={level > 0 ? "mt-1" : ""}
                  >
                    <button
                      onClick={() => {
                        if (!isSidebarOpen) setIsSidebarOpen(true);
                        toggleMenu(item.id || item.name);
                      }}
                      className={`w-full flex items-center justify-between transition-all ${
                        level === 0
                          ? "p-3 rounded-xl"
                          : "py-2 px-2 text-sm rounded-lg"
                      } ${
                        isActive && level === 0
                          ? "bg-slate-800"
                          : isActive && level > 0
                            ? "font-medium"
                            : "text-slate-400 hover:bg-white/5 hover:text-white"
                      }`}
                      style={{
                        ...(isActive ? { color: "var(--admin-primary)" } : {}),
                      }}
                    >
                      <div
                        className={`flex items-center ${level === 0 ? "gap-4" : "gap-2"}`}
                      >
                        {item.icon ? (
                          <item.icon
                            size={level === 0 ? 20 : 16}
                            className="shrink-0"
                          />
                        ) : (
                          <Dot
                            size={level === 0 ? 24 : 20}
                            strokeWidth={3}
                            className="shrink-0 opacity-40"
                          />
                        )}
                        {isSidebarOpen && (
                          <span
                            className={`${level === 0 ? "font-medium" : ""} whitespace-nowrap`}
                          >
                            {item.name}
                          </span>
                        )}
                      </div>
                      {isSidebarOpen &&
                        (isExpanded ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        ))}
                    </button>

                    <AnimatePresence>
                      {isSidebarOpen && isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div
                            className={`flex flex-col space-y-1 ${level === 0 ? "pl-2 pr-3 py-1 border-r-2 border-slate-800 mr-2 mt-1" : "pl-1 pr-2 py-1 border-r border-slate-700 mr-2"}`}
                          >
                            {item.subItems.map((subItem) =>
                              renderMenuItem(subItem, level + 1),
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              } else {
                return (
                  <Link
                    key={item.id || item.name}
                    to={item.path!}
                    className={`w-full flex items-center transition-all ${
                      level === 0
                        ? "gap-4 p-3 rounded-xl"
                        : "gap-2 py-2 px-2 text-sm rounded-lg"
                    } ${
                      isActive && level === 0
                        ? "shadow-lg shadow-black/10 text-white"
                        : isActive && level > 0
                          ? "font-medium"
                          : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                    }`}
                    style={{
                      ...(isActive && level === 0
                        ? { backgroundColor: "var(--admin-primary)" }
                        : {}),
                      ...(isActive && level > 0
                        ? {
                            backgroundColor:
                              "color-mix(in srgb, var(--admin-primary) 10%, transparent)",
                            color: "var(--admin-primary)",
                          }
                        : {}),
                    }}
                  >
                    {item.icon ? (
                      <item.icon
                        size={level === 0 ? 20 : 16}
                        className="shrink-0"
                      />
                    ) : (
                      <Dot
                        size={level === 0 ? 24 : 20}
                        strokeWidth={3}
                        className="shrink-0 opacity-40"
                      />
                    )}
                    {isSidebarOpen && (
                      <span
                        className={`${level === 0 ? "font-medium" : ""} whitespace-nowrap`}
                      >
                        {item.name}
                      </span>
                    )}
                  </Link>
                );
              }
            };

            return renderMenuItem(item);
          })}
        </nav>

        <button
          onClick={handleLogout}
          className="mt-8 mb-2 flex items-center gap-4 p-3 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-xl transition-all overflow-hidden shrink-0"
        >
          <LogOut size={20} className="shrink-0" />
          {isSidebarOpen && (
            <span className="font-medium whitespace-nowrap">خروج از حساب</span>
          )}
        </button>
      </motion.aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        {getPageTitle() !== "اطلاعات کالا" && (
          <header className={`flex justify-between items-center ${["کالاها", "دسته‌بندی‌ها", "پکیج", "بسته‌بندی‌ها", "سازندگان", "ثبت سفارش PCB", "PCB Order Registration"].includes(getPageTitle() || "") ? "mb-4" : "mb-10"}`}>
            <div>
              <h2 className={`${
                ["ثبت سفارش PCB", "PCB Order Registration"].includes(getPageTitle() || "")
                  ? "text-lg"
                  : ["کالاها", "دسته‌بندی‌ها", "پکیج", "بسته‌بندی‌ها", "سازندگان"].includes(getPageTitle() || "")
                  ? "text-xl"
                  : "text-3xl"
              } font-bold text-slate-800 dark:text-white mb-1`}>
                {getPageTitle()}
              </h2>
              <p className="text-slate-500 dark:text-slate-400">
                {getPageTitle() === "پیشخوان" && `خوش آمدید، ${username}`}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {location.pathname === "/admin/orders/pcb/add" && (
                <button
                  type="button"
                  onClick={togglePcbLanguage}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-[#2563eb] cursor-pointer shadow-sm shrink-0"
                >
                  <span className="font-mono text-[10px] bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-md font-bold">
                    {pcbLanguage.toUpperCase()}
                  </span>
                  <span>{pcbLanguage === "en" ? "فارسی" : "English"}</span>
                </button>
              )}
              <div className="mr-2">
                <ThemeToggle isDark={isDark} toggle={toggleTheme} />
              </div>
            </div>
          </header>
        )}

        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetails />} />
          <Route path="/products/packages" element={<Packages />} />
          <Route path="/products/packaging" element={<Packaging />} />
          <Route path="/products/manufacturers" element={<Manufacturers />} />
          <Route path="/settings/db-check" element={<DatabaseCheck />} />
          <Route path="/settings/db-backup" element={<DatabaseBackup />} />
          
          {/* PCB Order Routes */}
          <Route path="/orders/pcb/add" element={<PcbOrderForm language={pcbLanguage} setLanguage={setPcbLanguage} />} />
          <Route path="/orders/pcb/new-orders" element={<PcbOrdersList filterType="new" />} />
          <Route path="/orders/pcb/current" element={<PcbOrdersList filterType="current" />} />
          <Route path="/orders/pcb/all" element={<PcbOrdersList filterType="all" />} />
          
          {/* Placeholder for others */}
          <Route
            path="*"
            element={
              <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-slate-100 dark:border-slate-700 text-center">
                <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
                  در حال توسعه
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  این بخش به زودی تکمیل خواهد شد.
                </p>
              </div>
            }
          />
        </Routes>
      </main>
    </div>
  );
};
